import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { catchError, debounceTime, finalize, Subject, Subscription, tap } from 'rxjs';
import {
  AllInputTypes,
  FormInputConfig,
  ListValues
} from '../../../../../../../trash/shared/interfaces/form-input-config';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormComponent, ToastService } from '../../../../../../../trash/shared/components';
import { getIdFormConfig } from '../../../../../../../views/pages/adminstration/temp-common/utils';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  defaultPaginationParameters,
  ErrorOrError,
  handleApiErrors,
  ILookup,
  IPaginationResponse,
  Logger
} from '../../../../../../core';
import { IAssignScopesRequest, IRevokeScopesRequest } from '../../../dtos';
import { ScopeItem, ScopesService } from '../../../../scopes';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { AccountsApiClientProvider, IAccountsApiClient } from '../../../clients';
import { IValidationErrors } from '../../../../../../../trash/shared/interfaces/shared-interfaces';

@Component({
  selector: 'app-assign-revoke-scopes',
  imports: [ReactiveFormsModule, CommonModule, DialogModule, ButtonModule, FormsModule, FormComponent],
  templateUrl: './assign-revoke-scopes.component.html',
  styleUrl: './assign-revoke-scopes.component.scss'
})
export class AssignRevokeScopesComponent {
  isLoadingActionItem = false;
  form: FormInputConfig[] = [];
  formValues: any = null;
  accountId: string | undefined;
  validationErrors: IValidationErrors = {};
  validationEmit = false;
  scopes: ListValues[] = [];
  selectedScopes: ListValues[] = [];

  private subscriptions: Subscription[] = [];
  private searchSubject = new Subject<any>();

  private clientAccounts: IAccountsApiClient = inject(AccountsApiClientProvider).getClient();

  constructor(
    private _scopesService: ScopesService,
    public dataObj: DynamicDialogConfig,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private ref: DynamicDialogRef
  ) {}

  ngOnInit(): void {
    this.accountId = this.dataObj?.data?.item?.id;
    if (this.accountId) {
      this.formValues = this.dataObj?.data?.item;
    }
    if (this.dataObj?.data?.type == 'revoke') {
      this.getAccountScopes();
      Logger.debug('SELECTED SCOPES', this.selectedScopes);
    } else {
      this.getAccountScopes();
      this.getAllScopes();
    }
    this.initForm();

    this.searchSubject.pipe(debounceTime(1000)).subscribe((field: any) => {
      if (field.keyword.length >= 2 || field.keyword.length == 0) {
        if (field?.fieldName == 'scopes') {
          this.getAllScopes(field?.keyword);
          this.selectedScopes = this.selectedScopes.filter(
            (value: any, index: any, self: any) => index === self.findIndex((t: any) => t.value === value.value)
          );
        }
      }
    });
  }

  onFilterSelect(e: any) {
    this.searchSubject.next(e);
  }

  initForm() {
    this.form = [
      getIdFormConfig(this.dataObj?.data?.item?.id),
      {
        type: AllInputTypes.MultiSelect,
        name: 'scopes',
        label: 'Scopes',
        placeholder: 'Select Scopes',
        widthClass: 'col-12',
        listValues: this.dataObj?.data?.type == 'revoke' ? this.selectedScopes : this.scopes,
        defaultValue: null,
        validation: [
          {
            function: Validators.required,
            errorMessage: 'Please select Scope'
          }
        ]
      }
    ];
  }
  valueChanged(data: any) {
    this.formValues = { ...data };
  }

  onClose(data?: any) {
    this.ref.close(data);
  }
  emitValidation() {
    this.validationEmit = !this.validationEmit;
    this.cdr.detectChanges();
  }

  async getAllScopes(keyword: string = '') {
    let scopesSubscription: Subscription = (await this._scopesService.getAll(defaultPaginationParameters))
      .pipe(
        tap((res: IPaginationResponse<ScopeItem>) => this.processScopesResponse(res)),
        catchError((errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)),
        finalize(() => {})
      )
      .subscribe();

    this.subscriptions.push(scopesSubscription);
  }
  private processScopesResponse(response: IPaginationResponse<ScopeItem>) {
    Logger.debug('RESPONSE', response);
    let items: ILookup<string>[] = response.items;
    this.scopes = items.map((item: ILookup<string>) => {
      return {
        label: item.englishName,
        value: item.id
      };
    });
    if (this.dataObj?.data?.type === 'assign') {
      Logger.debug('EXISTING SCOPES', this.selectedScopes);
      Logger.debug('All SCOPES', this.scopes);

      const existingValues = this.selectedScopes.map((scope) => scope.value);
      Logger.debug('EXISTING VALUES', existingValues);

      const filteredScopes = this.scopes.filter((scope) => !existingValues.includes(scope.value));

      Logger.debug('FILETERED SCOPES', filteredScopes);
      this.scopes = filteredScopes;
    }
    this.getAccountScopes();
  }

  getAccountScopes(keyword: string = '') {
    this.clientAccounts.getScopes(this.accountId!).subscribe({
      next: (res: ILookup<string>[]) => this.processAccountScopesResponse(res),
      error: (errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)
    });
  }
  private processAccountScopesResponse(response: any): void {
    this.selectedScopes = response.items.map((item: ILookup<string>) => {
      return {
        label: item.englishName,
        value: item.id
      };
    });

    this.initForm();
    Logger.debug('SELECTED SCOPES from getAccountScopes', this.selectedScopes);
  }

  async submitForm(data: any) {
    this.isLoadingActionItem = true;
    if (this.dataObj?.data?.type == 'revoke') {
      const revokeData: IRevokeScopesRequest = this.prepareRevokeDataRequest(data);
      const subscription = this.clientAccounts
        .revokeScopes(this.formValues.id, revokeData)
        .pipe(
          tap((res: any) => {
            this.processAssignRevokeResponse(res);
          }),
          catchError((errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)),
          finalize(() => {
            this.isLoadingActionItem = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe();
      this.subscriptions.push(subscription);
    } else {
      const AssignData: IAssignScopesRequest = this.prepareAssignDataRequest(data);
      const subscription = this.clientAccounts
        .assignScopes(this.formValues.id, AssignData)
        .pipe(
          tap((res: any) => {
            this.processAssignRevokeResponse(res);
          }),
          catchError((errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)),
          finalize(() => {
            this.isLoadingActionItem = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe();
      this.subscriptions.push(subscription);
    }
  }
  private prepareAssignDataRequest(data: any): IAssignScopesRequest {
    return {
      scopeIds: data?.scopes
    };
  }

  private prepareRevokeDataRequest(data: any): IRevokeScopesRequest {
    return {
      scopeIds: data?.scopes
    };
  }
  private processAssignRevokeResponse(response: any) {
    if (this.dataObj?.data?.type == 'revoke') {
      this.toastService.showSuccess('Success', 'Revoking was is successfully');
    } else {
      this.toastService.showSuccess('Success', 'Assigning Scopes was successfully');
    }
    this.onClose(true);
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
